import {
  formatAmount,
  formatDateLocal,
} from "../../../../../utils/hooks/dataHandler";
import { FORM_CS_DECLARATION_ITEMS } from "../formCSDeclarationContent";
import { normalizeDataFormFieldValueForDisplay } from "../formCSDefaults";
import {
  SCTP_IND_OPTIONS,
  FS_PREP_OPTIONS,
  YES_NO_NUMBER_OPTIONS,
  YES_NO_BLANK_OPTIONS,
  YES_NO_YN_OPTIONS,
  TAX_ADJUSTMENT_GROUPS,
  BF_DONATION_GROUPS,
  PL_SUPPLEMENT_FIELDS,
  EIS_AMOUNT_FIELDS,
  getDataFormCSFieldMeta,
  getDataFormCSSectionMeta,
} from "../formCSDataFormFields";
import { formatTcOptionLabel, formatRefAmount } from "../formCSReferenceUtils";

const UEN_TYPE_LABELS = {
  0: "Local Companies with UEN (yyyynnnnnX format)",
  1: "Foreign Companies with UEN (TyyPQnnnnX format)",
  2: "Others without UEN",
};

const DROPDOWN_OPTION_BY_KEY = {
  sctpInd: SCTP_IND_OPTIONS,
  fsPrep: FS_PREP_OPTIONS,
  appStockConvAsset: YES_NO_NUMBER_OPTIONS,
  eis_ClaimCashPayout: YES_NO_BLANK_OPTIONS,
  eis_ClaimDedAll: YES_NO_BLANK_OPTIONS,
  sholderChange: YES_NO_YN_OPTIONS,
  uCALDChangePrinAct: YES_NO_YN_OPTIONS,
  fullTxX: YES_NO_YN_OPTIONS,
  ptisDonInd: YES_NO_YN_OPTIONS,
  foreignSourceSaleNotTax: YES_NO_YN_OPTIONS,
};

const NUMERIC_DATA_KEYS = new Set([
  "profitLossBeforeTaxation",
  "sepSrcIncome",
  "receiptNotTxAmt",
  "c1_NTDeductibleExp",
  "adjPLBefDed",
  "renoWorksExpS14Q",
  "c1_FurtherDed",
  "sgAdjPLAft",
  "c1_BC",
  "unutilCABFNorm",
  "cyCANorm",
  "unutilLossBFNorm",
  "c1_GrossRent",
  "c1_DedExp",
  "sgRent",
  "sgIntDisc",
  "sgOtherI",
  "totSgFI",
  "unutilDonationBFNorm",
  "cyDonation",
  "cyOverseasDonation",
  "ci",
  "unutilCACFNorm",
  "unutilLossCFNorm",
  "unutilDonationCFNorm",
  "totalRevenue",
  "grossPL",
  "directorFee",
  "totRemuneration",
  "medicalExp",
  "c1_TransportExp",
  "c1_EntertainExp",
  "inventories",
  "tradeReceivables",
  "unutilCALDClaimS23S37",
  "expRD",
  "expRDSG",
  "enhanceDeductRD",
  "c1_EnhancedEISDed",
  "foreignAssetsSaleGainLoss",
  "foreignSourceSaleGainsRemit",
  ...EIS_AMOUNT_FIELDS,
]);

const row = (label, value) => ({ label, value: value ?? "—" });

const formatYesNo = (checked) => (checked ? "Yes" : "No");

export const formatDataFormFieldValue = (key, value) => {
  const displayValue = normalizeDataFormFieldValueForDisplay(key, value);
  if (key === "foreignSourceSaleNotTax" && displayValue === "NA") {
    return "No";
  }
  if (DROPDOWN_OPTION_BY_KEY[key]) {
    const options = DROPDOWN_OPTION_BY_KEY[key];
    const match = options.find((o) => String(o.value) === String(displayValue));
    return (
      match?.label ??
      (displayValue == null || displayValue === "" ? "—" : String(displayValue))
    );
  }
  if (NUMERIC_DATA_KEYS.has(key)) {
    const n = Number(value);
    return Number.isFinite(n) ? formatAmount(n, false) : "—";
  }
  if (value == null || value === "") return "—";
  return String(value);
};

const dataRows = (dataFormCS, keys, getLabel) =>
  keys.map((key) =>
    row(
      getLabel ? getLabel(key) : getDataFormCSFieldMeta(key).label,
      formatDataFormFieldValue(key, dataFormCS[key])
    )
  );

const dataRow = (dataFormCS, key) =>
  row(
    getDataFormCSFieldMeta(key).label,
    formatDataFormFieldValue(key, dataFormCS[key])
  );

const TAX_FINAL_RESULT_PDF_KEYS = [
  "renoWorksExpS14Q",
  "c1_EnhancedEISDed",
  "c1_FurtherDed",
  "sgAdjPLAft",
  "c1_BC",
];

const getTaxAdjustmentPdfLabel = (key) => {
  return getDataFormCSFieldMeta(key).label;
};

const getRdPdfLabel = (key) => getDataFormCSFieldMeta(key).label;

const SECTION_10L_PDF_KEYS = [
  "foreignSourceSaleNotTax",
  "foreignAssetsSaleGainLoss",
  "foreignSourceSaleGainsRemit",
];

const SECTION_10L_PDF_DESCRIPTION_LABEL_KEYS = new Set([
  "foreignSourceSaleNotTax",
  "appStockConvAsset",
]);

const getSection10LPdfLabel = (key) =>
  SECTION_10L_PDF_DESCRIPTION_LABEL_KEYS.has(key)
    ? getDataFormCSFieldMeta(key).description
    : getDataFormCSFieldMeta(key).label;

const groupHeaderRow = (title, { isFirst = false } = {}) =>
  row(
    {
      content: title,
      styles: {
        fontStyle: "bold",
        cellPadding: { top: isFirst ? 2 : 6, bottom: 2, left: 0, right: 0 },
      },
    },
    ""
  );

const subtotalCell = (content) => ({
  content: content == null || content === "" ? "—" : String(content),
  styles: {
    fontStyle: "bold",
    valign: "middle",
  },
});

const subtotalDataRow = (dataFormCS, key) =>
  row(
    subtotalCell(getDataFormCSFieldMeta(key).label),
    subtotalCell(formatDataFormFieldValue(key, dataFormCS[key]))
  );

const BF_CURRENT_INCOME_PDF_GROUPS = [
  {
    title: "Opening balances",
    keys: ["unutilCABFNorm", "cyCANorm", "unutilLossBFNorm"],
  },
  {
    title: "Current period income",
    keys: ["c1_GrossRent", "c1_DedExp", "sgIntDisc", "sgOtherI"],
  },
];

const buildBfCurrentIncomePdfRows = (dataFormCS) => {
  const rows = [];
  BF_CURRENT_INCOME_PDF_GROUPS.forEach((group, index) => {
    rows.push(groupHeaderRow(group.title, { isFirst: index === 0 }));
    rows.push(...dataRows(dataFormCS, group.keys));
  });
  rows.push(dataRow(dataFormCS, "sgRent"));
  rows.push(subtotalDataRow(dataFormCS, "totSgFI"));
  return rows;
};

const BF_DONATION_INCOME_PDF_KEYS = [
  "unutilDonationBFNorm",
  "cyDonation",
  "cyOverseasDonation",
  "ptisDonInd",
];

const buildBfDonationIncomePdfRows = (dataFormCS) => [
  ...dataRows(dataFormCS, BF_DONATION_INCOME_PDF_KEYS),
  subtotalDataRow(dataFormCS, "ci"),
];

const sectionTitle = (key) => getDataFormCSSectionMeta(key).title;

const showEisExpenseRows = (dataFormCS = {}) =>
  dataFormCS.eis_ClaimCashPayout === "1" || dataFormCS.eis_ClaimDedAll === "1";

export const buildFilingInfoRows = (
  filingInfo = {},
  { isDevPdfExport = false } = {}
) => [
  row("UEN", filingInfo.uen),
  row(
    "UEN type",
    isDevPdfExport && String(filingInfo.uenType) === "6"
      ? "Local Companies with UEN"
      : (UEN_TYPE_LABELS[Number(filingInfo.uenType)] ?? filingInfo.uenType)
  ),
  row("Year of assessment (YA)", filingInfo.ya),
  row("Declarant reference no.", filingInfo.declarantRefNo || "—"),
  row("Designation of filer", filingInfo.designationOfFiler),
  row("Name of contact person", filingInfo.nameOfContactPerson),
  row("Contact number", filingInfo.contactNumber),
  row("Designation of contact person", filingInfo.designationOfContactPerson),
];

export const buildTaxComputationReferenceRows = (
  selectedTc,
  referenceSummary
) => {
  if (!selectedTc && !referenceSummary) return [];

  const rows = [];
  if (selectedTc) {
    rows.push(row("Tax computation", formatTcOptionLabel(selectedTc)));
    if (selectedTc.financialYearEnd) {
      try {
        rows.push(
          row(
            "Financial year end",
            formatDateLocal(new Date(selectedTc.financialYearEnd))
          )
        );
      } catch {
        rows.push(
          row("Financial year end", String(selectedTc.financialYearEnd))
        );
      }
    }
  }

  if (referenceSummary) {
    const cur = referenceSummary.currency || selectedTc?.currency || "SGD";
    rows.push(
      row("NPBT (reference)", formatRefAmount(cur, referenceSummary.npbt)),
      row(
        "Chargeable income after exempt",
        `SGD ${formatAmount(referenceSummary.chargebleIncomeAfterExempt)}`
      ),
      row(
        "Net tax payable (reference)",
        `SGD ${formatAmount(referenceSummary.netTaxPayable, false)}`
      )
    );
    if (referenceSummary.isStartup) {
      rows.push(row("Start-up tax exemption", "Applicable"));
    }
  }

  return rows;
};

const maskSctpNo = (value) => {
  if (!value) return "—";
  if (value.length <= 3) return value;
  return `${value.slice(0, 3)}${"*".repeat(value.length - 3)}`;
};

export const buildDataFormCSSections = (
  dataFormCS = {},
  { isDevPdfExport = false } = {}
) => {
  const sections = [];

  const basisRows = [
    row("Basis period from", dataFormCS.basisPeriodFrom || "—"),
    row("Basis period to", dataFormCS.basisPeriodTo || "—"),
    row(
      "Reviewed by SCTP",
      formatDataFormFieldValue("sctpInd", dataFormCS.sctpInd)
    ),
  ];
  if (Number(dataFormCS.sctpInd) === 1) {
    basisRows.push(
      row("SCTP name", dataFormCS.sctpName || "—"),
      row("SCTP membership no.", maskSctpNo(dataFormCS.sctpNo))
    );
  }
  basisRows.push(
    row(
      "Financial statements prepared",
      isDevPdfExport
        ? "-"
        : formatDataFormFieldValue("fsPrep", dataFormCS.fsPrep)
    ),
    row(
      "First set — financial year end",
      isDevPdfExport ? "-" : dataFormCS.firstFYE || "—"
    )
  );
  if (Number(dataFormCS.fsPrep) === 2) {
    basisRows.push(
      row("Previous financial year end", dataFormCS.prevFYE || "—")
    );
  }
  sections.push({
    title: sectionTitle("basisPeriod"),
    rows: basisRows,
  });

  TAX_ADJUSTMENT_GROUPS.forEach((group) => {
    const isTaxFinalResult = group.sectionKey === "taxFinalResult";
    sections.push({
      title: group.title,
      rows: dataRows(
        dataFormCS,
        isTaxFinalResult ? TAX_FINAL_RESULT_PDF_KEYS : group.keys,
        isTaxFinalResult ? getTaxAdjustmentPdfLabel : undefined
      ),
    });
  });

  BF_DONATION_GROUPS.filter((g) => g.variant !== "closing").forEach((group) => {
    const isBfCurrentIncome = group.layout === "income";
    const isBfDonationIncome = group.layout === "donation";
    sections.push({
      title: group.title,
      rows: isBfCurrentIncome
        ? buildBfCurrentIncomePdfRows(dataFormCS)
        : isBfDonationIncome
          ? buildBfDonationIncomePdfRows(dataFormCS)
          : dataRows(dataFormCS, group.keys),
    });
  });

  const closingKeys = BF_DONATION_GROUPS.find(
    (g) => g.variant === "closing"
  )?.keys;
  if (closingKeys?.length) {
    sections.push({
      title: sectionTitle("bfClosing"),
      rows: dataRows(dataFormCS, closingKeys),
    });
  }

  sections.push({
    title: sectionTitle("plSupplement"),
    rows: dataRows(dataFormCS, PL_SUPPLEMENT_FIELDS),
  });

  sections.push({
    title: sectionTitle("qualificationChanges"),
    rows: [
      dataRow(dataFormCS, "uCALDChangePrinAct"),
      dataRow(dataFormCS, "sholderChange"),
      dataRow(dataFormCS, "unutilCALDClaimS23S37"),
      dataRow(dataFormCS, "fullTxX"),
    ],
  });

  sections.push({
    title: sectionTitle("rd"),
    rows: dataRows(
      dataFormCS,
      ["expRD", "expRDSG", "enhanceDeductRD"],
      getRdPdfLabel
    ),
  });

  sections.push({
    title: sectionTitle("stockAppropriation"),
    rows: dataRows(dataFormCS, ["appStockConvAsset"], getSection10LPdfLabel),
  });

  sections.push({
    title: sectionTitle("section10L"),
    rows: dataRows(dataFormCS, SECTION_10L_PDF_KEYS, getSection10LPdfLabel),
  });

  const eisRows = [
    dataRow(dataFormCS, "eis_ClaimCashPayout"),
    dataRow(dataFormCS, "eis_ClaimDedAll"),
  ];
  if (showEisExpenseRows(dataFormCS)) {
    eisRows.push(...dataRows(dataFormCS, EIS_AMOUNT_FIELDS));
  }
  sections.push({ title: sectionTitle("eis"), rows: eisRows });

  return sections;
};

export const buildDeclarationRows = (declaration = {}, declarationAck = {}) => {
  const irasRows = FORM_CS_DECLARATION_ITEMS.map((item) =>
    row(
      item.optional ? `${item.label} (optional)` : item.label,
      formatYesNo(declaration[item.key])
    )
  );

  const ackRows = [
    row(
      "Final confirmation",
      formatYesNo(
        declarationAck.isAuthorisedAccurate &&
          declarationAck.hasConfirmedAllDeclarations
      )
    ),
    row("Declaration name", declarationAck.declarantName || "—"),
    row("Date of declaration", declarationAck.declarationDate || "—"),
  ];

  const submitterCapacity =
    declarationAck.submitterCapacity === "TAX_AGENT"
      ? "Authorised tax agent"
      : declarationAck.submitterCapacity === "TAXPAYER"
        ? "Company representative (direct taxpayer submission)"
        : "—";
  const taxAgentRows = [row("Submission Authority", submitterCapacity)];
  if (declarationAck.submitterCapacity === "TAX_AGENT") {
    taxAgentRows.push(
      row("Tax agent UEN", declarationAck.taxAgentUen || "—"),
      row(
        "Tax agent declaration",
        declarationAck.isTaxAgentAuthorised
          ? "Confirmed — authorised to act and submit Form C-S on the company's behalf"
          : "Not confirmed"
      )
    );
  }

  return { irasRows, taxAgentRows, ackRows };
};

/**
 * All PDF sections for Form C-S draft review (filing + data + declarations).
 */
export const buildFormCSDraftPdfSections = ({
  filingInfo,
  dataFormCS,
  declaration,
  declarationAck,
  selectedTc,
  referenceSummary,
  isDevPdfExport = false,
}) => {
  const { irasRows, taxAgentRows, ackRows } = buildDeclarationRows(
    declaration,
    declarationAck
  );
  const refRows = buildTaxComputationReferenceRows(
    selectedTc,
    referenceSummary
  );

  const sections = [
    {
      title: "Filing information",
      rows: buildFilingInfoRows(filingInfo, { isDevPdfExport }),
    },
  ];

  if (refRows.length > 0) {
    sections.push({
      title: "Tax computation reference (not submitted to IRAS)",
      rows: refRows,
    });
  }

  sections.push(...buildDataFormCSSections(dataFormCS, { isDevPdfExport }));
  sections.push({ title: "IRAS declarations", rows: irasRows });
  sections.push({
    title: "Submitter & tax agent declaration",
    rows: taxAgentRows,
  });
  sections.push({ title: "Signature & acknowledgement", rows: ackRows });

  return sections;
};
